import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Drag-and-drop reordering for table rows (@dnd-kit, ported from medispace).
//
//   <SortableRows items={items} onReorder={handleReorder} disabled={editing}>
//     <Table> ... <tbody>{items.map(it => <SortableTr key={it.id} id={it.id}>...cells</SortableTr>)}</tbody> </Table>
//   </SortableRows>
//
// SortableTr renders the row's FIRST cell itself (the ⋮⋮ drag handle) — add an
// empty <th style={{ width: 36 }} /> to the header and +1 any colSpans.
// onReorder receives the full reordered items array; persist it with the
// full-list ids (see reorderList in apiClient).

export { arrayMove };

export function SortableRows({ items, onReorder, disabled = false, children }) {
  // The 4px activation distance keeps plain clicks (buttons in the row) working.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  function handleDragEnd({ active, over }) {
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((it) => Number(it.id) === Number(active.id));
    const newIndex = items.findIndex((it) => Number(it.id) === Number(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    // Second arg: which row was dragged — consumers that move rows as GROUPS
    // (PC-sum pairs) anchor the group at the dragged row's new position.
    onReorder(arrayMove(items, oldIndex, newIndex), Number(active.id));
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext
        items={items.map((it) => Number(it.id))}
        strategy={verticalListSortingStrategy}
        disabled={disabled}
      >
        {children}
      </SortableContext>
    </DndContext>
  );
}

export function SortableTr({ id, disabled = false, className, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: Number(id),
    disabled,
  });
  const cls = [className, isDragging ? "table-active" : ""].filter(Boolean).join(" ");
  return (
    <tr
      ref={setNodeRef}
      className={cls || undefined}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <td className="text-center align-middle" style={{ width: 36 }}>
        <button
          type="button"
          className="btn btn-light btn-sm border-0 px-1 py-0"
          data-hint={disabled ? "Finish editing first" : "Drag to reorder"}
          disabled={disabled}
          style={{ cursor: disabled ? "default" : "grab", lineHeight: 1, touchAction: "none" }}
          {...attributes}
          {...listeners}
        >
          ⋮⋮
        </button>
      </td>
      {children}
    </tr>
  );
}
